import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, ImageUploadZone, Icons } from '@shopline/dashboard-ui';
import { Trans, useTranslation } from 'react-i18next';
import Divider from 'components/Divider';
import { Text } from 'components';
import Spacer from 'components/Spacer';
import usePermissions from '../../hooks/usePermissions';
import { Field, FormFooter, Input } from './components';
import { useQueryMerchant } from './useMerchant';
import useMerchantForm from './useMerchantForm';
import PARENT_E2E_PREFIX from './e2ePrefix';
import { env } from 'utils/env';
import { TextButton } from '@shopline/dashboard-ui';
import useAdminHost from 'hooks/useAdminHost';
import Alert from 'components/Alert';

const E2E_PREFIX = `${PARENT_E2E_PREFIX}-merchant-form`;

const StyledImageUploadZone = styled(ImageUploadZone)`
  > div {
    cursor: pointer;
    padding: 16px;
  }

  svg {
    fill: #596480;
  }
`;

const SelectedFileBox = styled.div`
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #d3dde6;
  border-radius: 6px;
  padding: 7px 15px;

  > div.fileName {
    &.hoverable:hover {
      cursor: pointer;
      text-decoration: underline;
    }
    display: flex;
    justify-content: left;
    align-items: center;

    > svg {
      padding-right: 8px;
    }
  }

  > div.trashCan {
    display: flex;
    cursor: pointer;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
  }
`;

const ValueBlock = styled.div`
  background-color: ${({ theme }) => theme.colors.SNOW_500};
  border-radius: 6px;
  padding: 16px;
`;

// @return object { name: string, content: string }
const processToken = (file) => {
  return new Promise((resolve) => {
    const loader = new FileReader();
    loader.onload = (loadEvent) => {
      const target = loadEvent.target || {};
      if (target.readyState !== 2) {
        resolve(null);
        return;
      }
      if (target.error) {
        console.error(target.error);
        resolve(null);
        return;
      }

      resolve({ name: file.name, content: target.result });
    };

    if (!file) {
      resolve(null);
      return;
    }
    loader.readAsText(file);
  });
};

const downloadText = (filename, text) => {
  const element = document.createElement('a');
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(text),
  );
  element.setAttribute('download', filename);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

const MerchantForm = (props) => {
  const { disabled = false } = props;
  const { t, i18n } = useTranslation(['main', 'common']);
  const canEdit = usePermissions('merchant', 'edit');
  const adminHost = useAdminHost();
  const { data: merchant } = useQueryMerchant();
  const [
    { preorderPeriod, contentApiToken },
    {
      errors,
      handlers,
      handleSubmit,
      hasErrors,
      credentialUploadFieldError,
      touched,
    },
    { renderMessages, submitting },
  ] = useMerchantForm({
    defaultValues: merchant,
  });
  const renderPreorderPeriodPreText = useCallback(() => t('UNIT.days'), [t]);
  const adminEmail = env('REACT_APP_GMC_ADMIN_EMAIL');
  const hasSelectedToken = contentApiToken?.content;
  const hasIntegrated =
    typeof merchant.googleMerchantId === 'string' &&
    merchant.googleMerchantId.length > 0;
  const initShowFileUpload = !merchant.contentApiIntegrated;
  const [showFileUpload, setShowFileUpload] = useState(initShowFileUpload);
  const removeToken = useCallback(() => {
    if (disabled) {
      return;
    }
    handlers.contentApiToken.onChange(null);
    handlers.contentApiToken.onBlur({ target: { value: null } });
    setShowFileUpload(true);
  }, [handlers, setShowFileUpload]);
  const setToken = useCallback(
    (token) => {
      handlers.contentApiToken.onFocus();
      handlers.contentApiToken.onChange(token);
      handlers.contentApiToken.onBlur({ target: { value: token } });
      setShowFileUpload(false);
    },
    [handlers, setShowFileUpload],
  );

  useEffect(() => {
    if (credentialUploadFieldError) {
      removeToken();
    }
  }, [credentialUploadFieldError, removeToken]);

  return (
    <>
      <div>
        <Field title={t('Merchant Center ID')}>
          <Text
            fontType="Body"
            color="INK_500"
            style={{ whiteSpace: 'pre-line' }}
          >
            {t('Merchant Center ID - description', {
              context: hasIntegrated ? 'integrated' : 'unintegrated',
            })}
          </Text>
          {hasIntegrated && (
            <>
              <Spacer y="16px" />
              <ValueBlock>
                <Text fontType="Body" color="INK_600">
                  {merchant.googleMerchantId}
                </Text>
              </ValueBlock>
            </>
          )}
          <Spacer y="16px" />
          <TextButton.Primary
            as="a"
            href={`${adminHost}/edit`}
            target="_blank"
            rel="noreferrer noopener"
            style={{ textDecoration: 'none' }}
          >
            <Icons.Outline.Link size="MEDIUM" />
            {t('Merchant Center ID - action button', {
              context: hasIntegrated ? 'integrated' : 'unintegrated',
            })}
          </TextButton.Primary>
        </Field>
      </div>
      <div>
        <Field title={t('Preorder Period')}>
          <Input
            block
            disabled={!canEdit || submitting || disabled}
            value={preorderPeriod}
            status={errors.preorderPeriod !== '' ? 'invalid' : 'valid'}
            renderMessages={renderMessages.preorderPeriod}
            e2eInputId={`${E2E_PREFIX}-preorder-period-input`}
            e2eMessagesId={`${E2E_PREFIX}-preorder-period-errors`}
            preTextPosition="RIGHT"
            renderPreText={renderPreorderPeriodPreText}
            {...handlers.preorderPeriod}
          />
        </Field>
      </div>
      <Divider spacing="32px" />
      <div>
        <Text fontType="Body" weight="Semibold">
          {t('Authorize Content API Title')}
        </Text>
        <Spacer y="4px" />
        <Text fontType="Body" color="INK_600">
          <Trans i18nKey="Authorize Content API Description" ns="main">
            link 1:
            <a
              href={t('Content API FAQ Link')}
              target="_blank"
              rel="noreferrer"
            >
              FAQ
            </a>
          </Trans>
        </Text>
        <Spacer y="16px" />
        {showFileUpload && (
          <StyledImageUploadZone
            disabled={disabled}
            isError={!!credentialUploadFieldError}
            onChange={(_, files) => processToken(files[0]).then(setToken)}
            onError={(e) => {
              console.error(e);
              setToken(null);
            }}
            lang={i18n.language}
            messages={{
              title: t('Upload Files Here'),
              error: t('Invalid Credential File'),
            }}
            accept="application/JSON"
            renderIcon={() => <Icons.Outline.Import size="XLARGE" />}
          />
        )}
        {!showFileUpload && (
          <SelectedFileBox>
            <div
              className={`fileName ${hasSelectedToken ? 'hoverable' : ''}`}
              onClick={() =>
                hasSelectedToken &&
                downloadText(contentApiToken.name, contentApiToken.content)
              }
            >
              <Icons.Solid.Note size="XLARGE" />
              {contentApiToken?.name || adminEmail}
            </div>
            <div className="trashCan" onClick={removeToken}>
              <Icons.Solid.Trash color="RED_600" size="MEDIUM" />
            </div>
          </SelectedFileBox>
        )}
      </div>
      <Spacer y="8px" />
      <Alert content={t('Save before leaving')} />
      <FormFooter>
        <Button.Primary
          width="WIDE"
          e2eId={`${E2E_PREFIX}-save-btn`}
          onClick={handleSubmit}
          disabled={!canEdit || !touched || hasErrors || submitting || disabled}
        >
          {t('Save', { ns: 'common' })}
        </Button.Primary>
      </FormFooter>
    </>
  );
};

export default MerchantForm;
